import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import {
  PATHOLOGY_FAX_NOTE_MAX_LENGTH,
  getDefaultPathologyFaxAction,
  pathologyFaxActionShowsFileSelection,
} from '@ldt/features/orders/utils/pathology_fax_action';
import { MultiSelectCheckboxGroup } from 'src/components/multi_select_checkbox_group';
import { RadioGroupField } from 'src/components/radio_group';
import {
  LdtOrderDetailFieldsFragment,
  PathologyFaxAction,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';

export type PathologyFaxConfirmPayload = {
  pathologyFaxAction: PathologyFaxAction;
  uploadedFileIds: string[];
  note: string | null;
};

interface ConfirmPathologyFaxDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: PathologyFaxConfirmPayload) => void;
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
  title: string;
  confirmButtonLabel?: string;
  isSubmitting?: boolean;
}

const PATHOLOGY_FAX_ACTION_OPTIONS = [
  {
    value: PathologyFaxAction.FaxPathologyFiles,
    name: 'Fax pathology files',
  },
  {
    value: PathologyFaxAction.SkipFaxGenerateFiles,
    name: 'Skip fax and generate pathology files',
  },
  {
    value: PathologyFaxAction.SkipFaxNoFiles,
    name: 'Skip fax and do not generate pathology files',
  },
] as const;

/**
 * Shared confirmation dialog for QC Approve (Clinic / Direct Upload) and
 * Pathology Fax Failed retry. Collects pathology fax action, optional note,
 * and optional pathology report / face sheet selection when files are
 * generated or faxed.
 */
export const ConfirmPathologyFaxDialog: FC<ConfirmPathologyFaxDialogProps> = ({
  open,
  onClose,
  onConfirm,
  order,
  orderRepository,
  title,
  confirmButtonLabel = 'CONFIRM',
  isSubmitting = false,
}) => {
  const { getFilesForCategory } = useLdtFileUploads({
    order,
    orderRepository,
  });

  const pathologyReportFiles = useMemo(
    () =>
      getFilesForCategory(UploadedFileCategory.PathologyReport).filter(
        (file) => file.status === UploadedFileStatus.Complete,
      ),
    [getFilesForCategory],
  );

  const faceSheetFiles = useMemo(
    () =>
      getFilesForCategory(UploadedFileCategory.FaceSheet).filter(
        (file) => file.status === UploadedFileStatus.Complete,
      ),
    [getFilesForCategory],
  );

  const pathologyReportOptions = useMemo(
    () =>
      pathologyReportFiles.map((file) => ({
        value: file.id,
        label: file.filename,
      })),
    [pathologyReportFiles],
  );

  const faceSheetOptions = useMemo(
    () =>
      faceSheetFiles.map((file) => ({
        value: file.id,
        label: file.filename,
      })),
    [faceSheetFiles],
  );

  const [pathologyFaxAction, setPathologyFaxAction] =
    useState<PathologyFaxAction>(() => getDefaultPathologyFaxAction(order));
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedFaceSheetIds, setSelectedFaceSheetIds] = useState<string[]>(
    [],
  );
  const [note, setNote] = useState('');

  const showFileSelection =
    pathologyFaxActionShowsFileSelection(pathologyFaxAction);

  useEffect(() => {
    if (!open) return;
    setPathologyFaxAction(getDefaultPathologyFaxAction(order));
    setSelectedReportIds([]);
    setSelectedFaceSheetIds([]);
    setNote('');
  }, [open, order]);

  useEffect(() => {
    if (!showFileSelection) {
      setSelectedReportIds([]);
      setSelectedFaceSheetIds([]);
    }
  }, [showFileSelection]);

  const handleConfirm = useCallback(() => {
    const trimmedNote = note.trim();
    onConfirm({
      pathologyFaxAction,
      uploadedFileIds: showFileSelection
        ? [...selectedReportIds, ...selectedFaceSheetIds]
        : [],
      note:
        showFileSelection && trimmedNote
          ? trimmedNote.slice(0, PATHOLOGY_FAX_NOTE_MAX_LENGTH)
          : null,
    });
  }, [
    note,
    onConfirm,
    pathologyFaxAction,
    showFileSelection,
    selectedFaceSheetIds,
    selectedReportIds,
  ]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const canConfirm =
    !isSubmitting && note.length <= PATHOLOGY_FAX_NOTE_MAX_LENGTH;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ textAlign: 'left' }}>{title}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack>
          <RadioGroupField
            name='pathologyFaxAction'
            value={pathologyFaxAction}
            options={[...PATHOLOGY_FAX_ACTION_OPTIONS]}
            onChange={(event) =>
              setPathologyFaxAction(event.target.value as PathologyFaxAction)
            }
          />

          {showFileSelection && (
            <>
              {pathologyReportFiles.length === 0 ? (
                <>
                  <Typography variant='subtitle2'>
                    Select Pathology Report
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    No pathology reports have been uploaded for this order.
                  </Typography>
                </>
              ) : (
                <MultiSelectCheckboxGroup
                  label='Select Pathology Report'
                  options={pathologyReportOptions}
                  value={selectedReportIds}
                  onChange={setSelectedReportIds}
                />
              )}

              {faceSheetFiles.length === 0 ? (
                <>
                  <Typography variant='subtitle2'>
                    Select Demographic / Face Sheet
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    No demographic / face sheet documents have been uploaded for
                    this order.
                  </Typography>
                </>
              ) : (
                <MultiSelectCheckboxGroup
                  label='Select Demographic / Face Sheet'
                  options={faceSheetOptions}
                  value={selectedFaceSheetIds}
                  onChange={setSelectedFaceSheetIds}
                />
              )}

              <TextField
                label='Fax Cover Sheet Notes'
                value={note}
                onChange={(event) => setNote(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                slotProps={{
                  htmlInput: { maxLength: PATHOLOGY_FAX_NOTE_MAX_LENGTH },
                  inputLabel: { shrink: true },
                }}
                helperText={`${note.length}/${PATHOLOGY_FAX_NOTE_MAX_LENGTH}`}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', gap: 2 }}>
        <Button
          onClick={handleClose}
          variant='text'
          size='large'
          disabled={isSubmitting}
        >
          CANCEL
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          size='large'
          disabled={!canConfirm}
        >
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
